import { Badge, Box, Flex, Overlay, Image, Avatar, Textarea, Button } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import theme from 'theme';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import H1SemiBold from 'theme/components/Typography/H1SemiBold';
import { Link, useParams } from 'react-router-dom';
import { IconChevronLeft, IconChevronRight, IconEye, IconFile, IconFileTypePdf, IconVideo } from '@tabler/icons-react';
import P2Medium from 'theme/components/Typography/P2Medium';
import { HELPDESK_PAGE_PATH } from 'routes/paths';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import P1Regular from 'theme/components/Typography/P1Regular';
import P1Medium from 'theme/components/Typography/P1Medium';
import { Modal } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import P2Regular from 'theme/components/Typography/P2Regular';
import { useApi } from 'api/api-context';
import panic from 'panic';
import { HelpdeskGetJobsResponse } from 'api/actions/helpdesk-get-jobs/helpdesk-get-jobs-response';
import { HelpdeskGetJobCommentsResponse } from 'api/actions/helpdesk-get-job-comments/helpdesk-get-job-comments-response';
import LoadingState from 'components/LoadingState';
import ErrorState from 'components/ErrorState';
import { HelpdeskGetJobAttachmentsResponse } from 'api/actions/helpdesk-get-job-attachments/helpdesk-get-job-attachments-response';

/**
 * HelpDeskDetail page
 */
const HelpDeskDetail: React.FC = () => {
  const { _t } = useLang();
  const { id } = useParams<{ id: string }>();
  const { getAction } = useApi();
  const jobId = Number(id);

  const [job, setJob] = useState<NonNullable<HelpdeskGetJobsResponse['jobs']>[0] | null>(null);
  const [comments, setComments] = useState<NonNullable<HelpdeskGetJobCommentsResponse['comments']>>([]);
  const [attachments, setAttachments] = useState<NonNullable<HelpdeskGetJobAttachmentsResponse['attachments']>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedFileType, setSelectedFileType] = useState<string>('image');
  const [isPlaying, setIsPlaying] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  /**
   * Load job details
   */
  const loadJobDetails = () => {
    const getJobs = getAction('HelpdeskGetJobs');
    getJobs()
      .then((response) => {
        // console.log('API Response:', response);

        // The API returns the jobs directly as an array, not wrapped in a jobs property
        const jobs = Array.isArray(response.data)
          ? response.data
          : (response.data as HelpdeskGetJobsResponse)?.jobs || [];
        // console.log('Jobs array:', jobs);
        // console.log('Looking for job ID:', jobId, 'Type:', typeof jobId);

        // Try both number and string comparison
        const foundJob = jobs.find((j) => {
          // console.log('Comparing job:', j.id, 'Type:', typeof j.id, 'with:', jobId);
          return j.id === jobId || Number(j.id) === jobId;
        });

        // console.log('Found job:', foundJob);
        setJob(foundJob || null);
        if (!foundJob) {
          setError('Job not found');
        }
        setLoading(false);
      })
      .catch((err) => {
        // console.error('Error loading job details:', err);
        setError('Failed to load job details');
        setLoading(false);
        panic(err);
      });
  };

  /**
   * Load job comments
   */
  const loadJobComments = () => {
    const getComments = getAction('HelpdeskGetJobComments');
    getComments({ parameters: { jobId: jobId.toString() } })
      .then((response) => {
        // The API returns the comments directly as an array, not wrapped in a comments property
        const commentsData = Array.isArray(response.data)
          ? response.data
          : (response.data as HelpdeskGetJobCommentsResponse)?.comments || [];

        // console.log('Comments data:', commentsData);
        setComments(commentsData);
      })
      .catch((err) => {
        // console.error('Error loading comments:', err);
        panic(err);
      });
  };

  /**
   * Load job attachments
   */
  const loadJobAttachments = () => {
    const getAttachments = getAction('HelpdeskGetJobAttachments');
    getAttachments({ parameters: { jobId: jobId.toString() } })
      .then((response) => {
        const attachmentsData = Array.isArray(response.data)
          ? response.data
          : (response.data as HelpdeskGetJobAttachmentsResponse)?.attachments || [];

        // console.log('Attachments data:', attachmentsData);
        setAttachments(attachmentsData);
      })
      .catch((err) => {
        // console.error('Error loading attachments:', err);
        panic(err);
      });
  };

  /**
   * getFileType function
   */
  function getFileType(input?: string): 'image' | 'pdf' | 'video' | 'other' {
    if (!input) return 'other';

    const mime = input.toLowerCase();

    if (mime.startsWith('image/')) {
      return 'image';
    }

    if (mime === 'application/pdf') {
      return 'pdf';
    }

    if (mime.startsWith('video/')) {
      return 'video';
    }

    // fallback na názov súboru ak `type_name` nie je k dispozícii
    if (mime.endsWith('.pdf')) {
      return 'pdf';
    }

    if (/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/.test(mime)) {
      return 'image';
    }

    if (/\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)$/.test(mime)) {
      return 'video';
    }

    return 'other';
  }

  /**
   * Render file icon based on file type
   */
  const renderFileIcon = (fileType: string, size: string = '2rem') => {
    switch (fileType) {
      case 'pdf':
        return <IconFileTypePdf size={size} color={theme.colors?.primary?.[0]} />;
      case 'video':
        return <IconVideo size={size} color={theme.colors?.primary?.[0]} />;
      default:
        return <IconFile size={size} color={theme.colors?.primary?.[0]} />;
    }
  };

  /**
   * Render attachment preview based on file type
   */
  const renderAttachmentPreview = (filePath: string, fileName: string, fileType: string) => {
    if (fileType === 'image') {
      return (
        <Image
          src={filePath}
          alt={fileName}
          style={{ borderRadius: '10px', objectFit: 'cover', width: '100%', height: '100%' }}
        />
      );
    } else if (fileType === 'video') {
      return (
        <Box
          style={{
            borderRadius: '10px',
            width: '100%',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <video
            src={filePath}
            style={{
              objectFit: 'cover',
              width: '100%',
              height: '100%',
              borderRadius: '10px',
            }}
            preload="metadata"
          />
          <Flex
            align="center"
            justify="center"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: theme.colors?.gray?.[1],
              borderRadius: '10px',
            }}
          >
            {renderFileIcon(fileType)}
          </Flex>
        </Box>
      );
    } else {
      return (
        <Flex
          align="center"
          justify="center"
          style={{
            borderRadius: '10px',
            backgroundColor: theme.colors?.gray?.[1],
            width: '100%',
            height: '100%',
          }}
        >
          {renderFileIcon(fileType)}
        </Flex>
      );
    }
  };

  /**
   * Click handler for attachments
   */
  const handleAttachmentClick = (src: string, fileType: string) => {
    setSelectedImage(src);
    setSelectedFileType(fileType);
    setIsPlaying(false);
    setOpened(true);
  };

  useEffect(() => {
    if (jobId) {
      setLoading(true);
      setError(null);
      loadJobDetails();
      loadJobComments();
      loadJobAttachments();
    }
  }, [jobId, getAction]);

  /**
   * Handle submitting a new comment
   */
  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;

    try {
      setIsSubmittingComment(true);
      // console.log('Submitting comment:', commentText, 'for job ID:', jobId);

      const postJobComment = getAction('PostJobComment');

      await postJobComment({
        parameters: { jobId: jobId.toString() },
        payload: {
          comment: commentText,
          isPublic: true,
        },
      });

      // console.log('Comment submitted successfully');

      // Clear the comment text
      setCommentText('');

      // Reload comments to show the new one
      loadJobComments();

      // Show success notification
      notifications.show({
        title: _t('Comment sent'),
        message: '',
        color: 'green',
      });
    } catch (error) {
      // console.error('Error submitting comment:', error);

      // Show error notification
      notifications.show({
        title: _t('Error'),
        message: _t('Failed to send comment. Please try again.'),
        color: 'red',
      });

      panic(error instanceof Error ? error : new Error(String(error)));
    } finally {
      setIsSubmittingComment(false);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  if (error || !job) {
    return (
      <ErrorState
        error={error}
        onRetry={() => {
          setLoading(true);
          setError(null);
          loadJobDetails();
          loadJobComments();
          loadJobAttachments();
        }}
        backPath={HELPDESK_PAGE_PATH.original}
        backLabel="Back to Helpdesk"
      />
    );
  }

  return (
    <SimpleLayout>
      <Box p="1rem" bg={theme.white}>
        <Flex direction="row" align="center">
          <Link
            to={HELPDESK_PAGE_PATH.original}
            style={{ textDecoration: 'none', display: 'flex', alignContent: 'center', marginBottom: '0.5rem' }}
          >
            <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
            <P2Medium color={theme.colors?.gray?.[9]}>{_t('Back to Helpdesk')}</P2Medium>
          </Link>
        </Flex>
        <H1SemiBold mt="0.75rem" color={theme.colors?.gray?.[12]}>
          {job.name}
        </H1SemiBold>
        <P1Regular mt="0.25rem" pb="1rem" color={theme.colors?.gray?.[12]}>
          {job.description}
        </P1Regular>
        <Flex
          py="1rem"
          gap="0.75rem"
          direction="column"
          style={{ borderTop: '1px solid #DFE2E6', borderBottom: '1px solid #DFE2E6' }}
        >
          <H2SemiBold color={theme.colors?.gray?.[12]}>{_t('Information')}</H2SemiBold>

          <Flex direction="row" justify="space-between">
            <P1Regular color={theme.colors?.gray?.[10]}>{_t('Date of reporting')}</P1Regular>
            <P1Medium color={theme.colors?.gray?.[12]}>
              {job.createdAt ? new Date(job.createdAt).toLocaleString('sk-SK') : ''}
            </P1Medium>
          </Flex>
          <Flex direction="row" justify="space-between">
            <P1Regular color={theme.colors?.gray?.[10]}>{_t('Reported')}</P1Regular>
            <P1Medium color={theme.colors?.gray?.[10]}>{job.reporterName}</P1Medium>
          </Flex>
          <Flex direction="row" justify="space-between">
            <P1Regular color={theme.colors?.gray?.[10]}>{_t('Location')}</P1Regular>
            <Badge key={id} variant="white">
              {job.targetObjectName}
            </Badge>
          </Flex>
          <Flex direction="row" justify="space-between">
            <P1Regular color={theme.colors?.gray?.[10]}>{_t('Status')}</P1Regular>
            <Badge key={id} variant={getStatusVariant(job.status ?? '')}>
              {_t(formatStatus(job.status ?? ''))}
            </Badge>
          </Flex>
        </Flex>
        <Box py="1rem">
          <H2SemiBold color={theme.colors?.gray?.[12]}>{_t('Files')}</H2SemiBold>
          <Flex mt="0.75rem" gap="10px" py="0.5rem">
            {attachments.map((file: any, index) => {
              // Create a unique identifier for each attachment
              const attachmentId = `attachment-${index}`;
              const filePath = file.path;
              const fileName = file.name;
              const fileType = getFileType(file.name);
              // console.log('Attachment:', filePath, fileName, fileType);
              return (
                <Box
                  key={index}
                  style={{ width: '88px', height: '88px', position: 'relative' }}
                  onMouseEnter={() => setHovered(attachmentId)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => handleAttachmentClick(filePath, fileType)}
                >
                  {renderAttachmentPreview(filePath, fileName, fileType)}
                  {hovered === attachmentId && (
                    <Overlay style={{ borderRadius: '10px' }} center>
                      <IconEye color={theme.white} />
                    </Overlay>
                  )}
                </Box>
              );
            })}
          </Flex>
        </Box>
        <Flex
          py="1rem"
          gap="1.5rem"
          direction="column"
          style={{ borderTop: '1px solid #DFE2E6', borderBottom: '1px solid #DFE2E6' }}
          display={job.type === 4 ? 'block' : 'none'}
        >
          <H2SemiBold color={theme.colors?.gray?.[12]}>{_t('Comments')}</H2SemiBold>
          {comments
            ?.sort((a, b) => new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime())
            .map((comment, index) => (
              <Flex key={index} gap="0.75rem">
                <Avatar size="2.75rem">{comment.creatorName?.[0] || 'U'}</Avatar>
                <Flex direction="column">
                  <P1Medium color={theme.black}>{comment.creatorName}</P1Medium>
                  <P2Regular color={theme.colors?.gray?.[9]}>
                    {comment.createdAt ? new Date(comment.createdAt).toLocaleString('sk-SK') : ''}
                  </P2Regular>
                  <P1Regular color={theme.colors?.gray?.[12]} pt="0.5rem">
                    {comment.text}
                  </P1Regular>
                </Flex>
              </Flex>
            ))}
        </Flex>
      </Box>

      <Modal opened={opened} onClose={() => setOpened(false)} centered size="lg">
        {selectedFileType === 'image' && <img src={selectedImage} alt={_t('Selected')} width="100%" />}
        {selectedFileType === 'pdf' && (
          <iframe
            src={`${selectedImage}#toolbar=0&navpanes=0`}
            width="100%"
            height="500px"
            style={{ border: 'none' }}
            title={_t('PDF viewer')}
          />
        )}
        {selectedFileType === 'video' && (
          <Box>
            <video
              src={selectedImage}
              width="100%"
              height="auto"
              controls
              autoPlay={isPlaying}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              style={{ maxHeight: '500px' }}
            />
          </Box>
        )}
        {selectedFileType !== 'image' && selectedFileType !== 'pdf' && selectedFileType !== 'video' && (
          <Flex direction="column" align="center" justify="center" p="2rem">
            {renderFileIcon(selectedFileType, '3rem')}
            <P1Medium mt="1rem">{_t('This file type cannot be previewed')}</P1Medium>
            <Button
              variant="outline"
              mt="1rem"
              component="a"
              href={selectedImage}
              target="_blank"
              rel="noopener noreferrer"
            >
              {_t('Download File')}
            </Button>
          </Flex>
        )}
      </Modal>
      <Box style={{ position: 'sticky', bottom: '0rem' }} w="100%" display={job.type === 4 ? 'block' : 'none'}>
        <Textarea
          placeholder={_t('Write a comment ...')}
          size="md"
          w="100%"
          pt="1rem"
          px="1rem"
          bg={theme.white}
          value={commentText}
          onChange={(e) => setCommentText(e.currentTarget.value)}
          style={{
            borderRadius: '10px 10px 0px  0px',
            borderTop: '1px solid #DFE2E6',
            boxShadow: '0px -4px 12px 0px #00000033',
          }}
          disabled={isSubmittingComment}
        />
        <Flex bg={theme.white} justify="end" align="center" pb="1rem" px="1rem" pt="0.5rem">
          <Button
            variant="primary"
            rightSection={<IconChevronRight />}
            onClick={handleSubmitComment}
            loading={isSubmittingComment}
            disabled={!commentText.trim() || isSubmittingComment}
          >
            {_t('Send')}
          </Button>
        </Flex>
      </Box>
    </SimpleLayout>
  );
};

/**
 * Get status variant for badge
 */
const getStatusVariant = (status: string | number): string => {
  switch (status.toString()) {
    case '0': // Unknown
      return 'gray';
    case '1': // Zadaná
      return 'blue';
    case '2': // Rozpracovaná
      return 'yellow';
    case '3': // Zrealizovaná
      return 'orange';
    case '4': // Hotová
      return 'green';
    case '5': // Zrušená
      return 'gray';
    default: // Neznáma / chyba
      return 'red';
  }
};

/**
 * Format status text
 */
const formatStatus = (status: string | number): string => {
  switch (status.toString()) {
    case '0':
      return 'Unknown';
    case '1':
      return 'Submitted';
    case '2':
      return 'In progress';
    case '3':
      return 'Implemented';
    case '4':
      return 'Done';
    case '5':
      return 'Canceled';
    default:
      return status.toString();
  }
};

export default HelpDeskDetail;
